import Konva from 'konva';
import { MEASUREMENT_LABELS } from './soil-survey-constants';

export const getMeasurementLabel = (code: string) => {
    return MEASUREMENT_LABELS[code] || code;
};

export const calculateOptimalStepSize = (maxValue: number, targetSteps = 7): { stepSize: number; stepCount: number } => {
    if (maxValue <= 0) return { stepSize: 0, stepCount: 0 };

    const niceSteps = [1, 2, 5];
    for (let i = 1; i <= 100; i++) {
        niceSteps.push(i * 10);
    }

    // Find the step size that gives closest to targetSteps
    let bestStep = niceSteps[0];
    let bestStepCount = Math.ceil(maxValue / bestStep);
    let bestDifference = Math.abs(bestStepCount - targetSteps);

    for (const step of niceSteps) {
        const stepCount = Math.ceil(maxValue / step);
        const difference = Math.abs(stepCount - targetSteps);

        if (difference < bestDifference) {
            bestStep = step;
            bestStepCount = stepCount;
            bestDifference = difference;
        }

        // Perfect match found
        if (difference === 0) break;

        // Stop if steps are getting too few (optimization)
        if (stepCount < targetSteps - 2) break;
    }

    return { stepSize: bestStep, stepCount: bestStepCount };
};

export const getSoilLayerColor = (soilType: string): string => {
    // Base colors from the standard Finnish soil classification chart
    const baseColors: { [key: string]: string } = {
        // Organic soils (Eloperäiset maalajit) - Gray tones
        Hm: 'rgb(192, 192, 192)', // Humusmaa - RGB 192 192 192
        Tv: 'rgb(192, 192, 192)', // Turve (Peat) - RGB 192 192 192
        RTv: 'rgb(192, 192, 192)', // Raakaturve
        KTv: 'rgb(192, 192, 192)', // Keskinkertaisesti maatunut turve
        MTv: 'rgb(192, 192, 192)', // Maatunut turve
        Lj: 'rgb(146, 146, 174)', // Lieju (Mud, ooze) - RGB 146 146 174
        siLj: 'rgb(146, 146, 174)',
        saLj: 'rgb(146, 146, 174)',

        // Fine-grained soils (Hienorakeiset maalajit) - Blue and Magenta
        Sa: 'rgb(146, 210, 254)', // Savi (Clay) - RGB 146 210 254
        laSa: 'rgb(146, 210, 254)', // Laiha savi
        liSa: 'rgb(146, 210, 254)', // Lihava savi
        ljSa: 'rgb(146, 210, 254)', // Liejuinen savi
        Si: 'rgb(211, 3, 255)', // Siltti (Silt) - RGB 211 3 255
        hSi: 'rgb(211, 3, 255)', // Hieno siltti
        keSi: 'rgb(211, 3, 255)', // Keskirakeinen siltti
        kaSi: 'rgb(211, 3, 255)', // Karkea siltti
        hkSi: 'rgb(211, 3, 255)', // Hiekkainen siltti
        saSi: 'rgb(211, 3, 255)', // Savinen siltti
        ljSi: 'rgb(211, 3, 255)', // Liejuinen siltti

        // Coarse-grained soils (Karkeakarekeiset maalajit) - Yellow and Green
        Hk: 'rgb(240, 234, 82)', // Hiekka (Sand) - RGB 240 234 82
        hHk: 'rgb(240, 234, 82)', // Hieno hiekka
        keHk: 'rgb(240, 234, 82)', // Keskirakeinen hiekka
        kaHk: 'rgb(240, 234, 82)', // Karkea hiekka
        srHk: 'rgb(240, 234, 82)', // Sorainen hiekka
        siHk: 'rgb(240, 234, 82)', // Silttinen hiekka
        huHk: 'rgb(240, 234, 82)', // Humuksinen hiekka
        Sr: 'rgb(113, 219, 113)', // Sora (Gravel) - RGB 113 219 113
        hSr: 'rgb(113, 219, 113)', // Hieno sora
        keSr: 'rgb(113, 219, 113)', // Keskirakeinen sora
        kaSr: 'rgb(113, 219, 113)', // Karkea sora
        hkSr: 'rgb(113, 219, 113)', // Hiekkainen sora
        huSr: 'rgb(113, 219, 113)', // Humuksinen sora

        // Moraine (Moreenimaolajit) - Orange/Brown
        Mr: 'rgb(218, 173, 48)', // Moreeni - RGB 218 173 48
        SrMr: 'rgb(218, 173, 48)', // Soramoreeni
        hkSrMr: 'rgb(218, 173, 48)', // Hiekkainen soramoreeni
        HkMr: 'rgb(218, 173, 48)', // Hiekkamoreeni
        siHkMr: 'rgb(218, 173, 48)', // Silttinen hiekkamoreeni
        srHkMr: 'rgb(218, 173, 48)', // Sorainen hiekkamoreeni
        huHkMr: 'rgb(218, 173, 48)', // Humuksinen hiekkamoreeni
        SiMr: 'rgb(218, 173, 48)', // Silttimoreeni
        hkSiMr: 'rgb(218, 173, 48)', // Hiekkainen silttimoreeni
        huSiMr: 'rgb(218, 173, 48)', // Humuksinen silttimoreeni

        // Other materials
        Mu: 'rgb(146, 146, 174)', // Multa (similar to Lieju)
        Ta: 'rgb(192, 192, 192)', // Täyttö (Fill - similar to organic)
        Ki: 'rgb(128, 128, 128)', // Kivet (Rocks - gray)
        Ka: 'rgb(100, 100, 100)', // Kallio (Bedrock - dark gray)
    };

    return baseColors[soilType] || '';
};

export const drawBars = (layer: Konva.Layer, x1: number, x2: number, y1: number, y2: number, lineOptions?: Partial<Konva.LineConfig>) => {
    /*
    const horizontalLine = new Konva.Line({
        points: [x1, y1, x2, y1],
        stroke: 'black',
        strokeWidth: 1.0,
        ...lineOptions
    });
    layer.add(horizontalLine);

    const verticalLine = new Konva.Line({
        points: [x2, y1, x2, y2],
        stroke: 'black',
        strokeWidth: 1.0,
        ...lineOptions
    });
    layer.add(verticalLine);
    */
    const verticalLine = new Konva.Line({
        points: [x1, y1, x1, y2],
        stroke: 'black',
        strokeWidth: 1.0,
        ...lineOptions,
    });
    layer.add(verticalLine);
    const horizontalLine = new Konva.Line({
        points: [x1, y2, x2, y2],
        stroke: 'black',
        strokeWidth: 1.0,
        ...lineOptions,
    });
    layer.add(horizontalLine);
};

export const drawLine = (layer: Konva.Layer, x1: number, x2: number, y1: number, y2: number, lineOptions?: Partial<Konva.LineConfig>) => {
    const defaultOptions: Konva.LineConfig = {
        points: [x1, y1, x2, y2],
        stroke: 'black',
        strokeWidth: 1.0,
    };

    const finalOptions = { ...defaultOptions, ...lineOptions };

    const line = new Konva.Line(finalOptions);
    layer.add(line);
};
